import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSocketContext } from '../../context/SocketContext';

const ServerClock = () => {
  const [serverTime, setServerTime] = useState(new Date());
  const [serverTimeObj, setServerTimeObj] = useState(null);
  const [lastSync, setLastSync] = useState(new Date());
  const [syncError, setSyncError] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const { socket, isConnected } = useSocketContext();
  const clockRef = useRef(null);
  
  // Function to request time sync from server
  const requestTimeSync = useCallback(() => {
    if (!isConnected || !socket) return;
    
    setIsSyncing(true);
    socket.emit('request:time');
    
    // Reset syncing state after visual feedback duration
    setTimeout(() => setIsSyncing(false), 800);
  }, [socket, isConnected]);
  
  // Handle keyboard events for accessibility
  const handleKeyDown = useCallback((e) => {
    // Trigger sync on Enter or Space key
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      requestTimeSync();
    }
  }, [requestTimeSync]);
  
  useEffect(() => {
    if (!socket) {
      setSyncError(true);
      return;
    }
    
    // Listen for server time updates
    socket.on('server:time', (data) => {
      if (data && data.time) {
        setServerTime(new Date(data.time));
        setLastSync(new Date());
        setSyncError(false);
        setIsSyncing(false);
        
        // Store time object if available
        if (data.timeObj) {
          setServerTimeObj(data.timeObj);
        }
      }
    });
    
    // Request initial time if socket is connected and we don't have it yet
    if (isConnected) {
      requestTimeSync();
    }
    
    // Update local clock every second based on the last known server time
    const clockIntervalId = setInterval(() => {
      if (isConnected && !syncError) {
        setServerTime(prevTime => new Date(prevTime.getTime() + 1000));
      }
    }, 1000);
    
    // Clean up interval and socket listener on component unmount
    return () => {
      clearInterval(clockIntervalId);
      socket.off('server:time');
    };
  }, [socket, isConnected, syncError, requestTimeSync]);
  
  // Format time with proper spacing for 12-hour format with AM/PM
  const formatTimeDigits = () => {
    if (!isConnected) return 'Disconnected';
    
    // Convert to 12-hour format
    let hours = serverTime.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // Convert 0 to 12
    
    // Format the time string
    const hoursStr = hours < 10 ? `\u00A0${hours}` : `${hours}`; // Use non-breaking space for padding
    const minutes = serverTime.getMinutes().toString().padStart(2, '0');
    const seconds = serverTime.getSeconds().toString().padStart(2, '0');
    
    // Create a simple time string
    return (
      <span className="time-digits">
        <span className="time-display">{hoursStr}:{minutes}:{seconds}</span>
        <span className="time-ampm">{ampm}</span>
      </span>
    );
  };
  
  // Calculate drift between client time and server time
  const getTimeDrift = () => {
    if (!serverTimeObj) return null;
    
    const now = new Date();
    const serverTimeMs = new Date(serverTime).getTime();
    const localTimeMs = now.getTime();
    const driftMs = Math.abs(localTimeMs - serverTimeMs);
    
    // If drift is more than 3 seconds, show warning
    if (driftMs > 3000) {
      return {
        drift: Math.round(driftMs / 1000),
        hasSignificantDrift: true
      };
    }
    
    return { drift: 0, hasSignificantDrift: false };
  };
  
  // Get time since last sync with server
  const getTimeSinceSync = () => {
    const now = new Date();
    const diffSeconds = Math.floor((now - lastSync) / 1000);
    return diffSeconds;
  };
  
  // Calculate if we need to show a drift warning
  const drift = getTimeDrift();
  const timeSinceSync = getTimeSinceSync();
  const showDriftWarning = drift?.hasSignificantDrift && timeSinceSync > 60;
  
  // Get the tooltip text
  const getTooltipText = () => {
    if (!isConnected) return 'Disconnected from server - Click to attempt reconnection';
    
    if (isSyncing) return 'Synchronizing with server...';
    
    if (showDriftWarning) {
      return `Time drift detected (~${drift.drift}s). Last sync: ${timeSinceSync}s ago. Click to resync.`;
    }
    
    const syncText = timeSinceSync < 60 
      ? `Last synced ${timeSinceSync} seconds ago` 
      : `Last synced ${Math.floor(timeSinceSync / 60)} min ${timeSinceSync % 60}s ago`;
      
    return `Server time synchronized. ${syncText}. Click to refresh.`;
  };
  
  // Get the appropriate icon based on state
  const getClockIcon = () => {
    if (!isConnected) return 'bi-cloud-slash';
    if (isSyncing) return 'bi-arrow-repeat spin';
    if (showDriftWarning) return 'bi-exclamation-triangle';
    return 'bi-clock';
  };
  
  // Get any additional classes
  const getAdditionalClasses = () => {
    let classes = '';
    if (!isConnected) classes += ' text-danger';
    if (showDriftWarning) classes += ' text-warning';
    if (isSyncing) classes += ' syncing';
    return classes;
  };
  
  // Get ARIA live state
  const getAriaLive = () => {
    if (isSyncing) return 'polite';
    if (showDriftWarning) return 'polite';
    return 'off';
  };
  
  // Format current time for screenreaders with AM/PM format
  const getAriaLabel = () => {
    if (!isConnected) return 'Server disconnected. Click to reconnect.';
    
    if (isSyncing) return 'Synchronizing server time...';
    
    const hours24 = serverTime.getHours();
    const ampm = hours24 >= 12 ? 'PM' : 'AM';
    const hours12 = hours24 % 12 || 12; // Convert 0 to 12
    const minutes = serverTime.getMinutes();
    const seconds = serverTime.getSeconds();
    
    let timeString = `Server time: ${hours12} ${hours12 === 1 ? 'hour' : 'hours'}, `;
    timeString += `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}, `;
    timeString += `${seconds} ${seconds === 1 ? 'second' : 'seconds'} ${ampm}`;
    
    return showDriftWarning ? `${timeString}. Warning: Time drift detected.` : timeString;
  };
  
  return (
    <div 
      ref={clockRef}
      className={`server-clock${getAdditionalClasses()}`}
      title={getTooltipText()}
      onClick={requestTimeSync}
      onKeyDown={handleKeyDown}
      tabIndex="0"
      role="button"
      aria-label={getAriaLabel()}
      aria-live={getAriaLive()}
    >
      <i className={`bi ${getClockIcon()}`}></i>
      {formatTimeDigits()}
    </div>
  );
};

export default ServerClock; 